import { Shield, Mail, MapPin, User } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { COMPANY, legal } from '@/i18n/legal';

function ControllerCard({ locale }) {
  return (
    <Card
      className="bg-card border border-border rounded-2xl p-5 sm:p-6 my-4"
      data-testid="legal-controller-card"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary shrink-0">
          <Shield className="h-4 w-4" />
        </div>
        <div className="text-sm leading-relaxed">
          <div className="font-display text-base text-foreground">{COMPANY.legalName}</div>
          <div className="mt-2 text-muted-foreground flex items-start gap-1.5">
            <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              {COMPANY.street}
              <br />
              {COMPANY.city}, {COMPANY.country[locale]}
            </span>
          </div>
          <div className="mt-2 text-muted-foreground flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" />
            <span>
              {legal[locale].contact.responsibleLabel}: {COMPANY.responsible}
            </span>
          </div>
          <div className="mt-2 text-muted-foreground flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" />
            <a
              href={`mailto:${COMPANY.email}`}
              className="hover:text-foreground transition-colors"
            >
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function LegalSections({ sections, locale, sectionTestIdPrefix }) {
  return (
    <div className="space-y-10">
      {sections.map((section, idx) => (
        <section
          key={section.id}
          id={section.id}
          data-testid={`${sectionTestIdPrefix}-${section.id}`}
          aria-labelledby={`heading-${section.id}`}
        >
          <h2
            id={`heading-${section.id}`}
            className="font-display text-xl sm:text-2xl tracking-[-0.01em] text-foreground"
          >
            {section.title}
          </h2>
          {section.body && (
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground whitespace-pre-line">
              {section.body}
            </p>
          )}

          {section.contactBlock && <ControllerCard locale={locale} />}

          {section.bullets && (
            <ul className="mt-4 space-y-2 text-sm sm:text-base text-muted-foreground">
              {section.bullets.map((b, i) => (
                <li key={`${section.id}-bullet-${i}`} className="flex items-start gap-2.5">
                  <span
                    aria-hidden="true"
                    className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0"
                  />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          )}

          {section.footer && (
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground/90">
              {section.footer}
            </p>
          )}

          {idx < sections.length - 1 && <Separator className="mt-10 bg-border/60" />}
        </section>
      ))}
    </div>
  );
}
